import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  BookOpen,
  Users,
  Brain,
  Calendar,
  FileText,
  Shield,
  CheckCircle,
  AlertTriangle,
  Award,
  UserCheck,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Reusable building blocks
// ---------------------------------------------------------------------------

const SectionTitle = ({ children }) => (
  <h4 className="font-semibold text-slate-900 mt-5 mb-1.5">{children}</h4>
);

const Shot = ({ src, alt, caption }) => (
  <figure className="my-3">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full border border-slate-200 rounded-lg shadow-sm"
    />
    <figcaption className="text-xs text-slate-500 mt-1.5 italic text-center">
      {caption}
    </figcaption>
  </figure>
);

const Callout = ({ tone = "info", title, children }) => {
  const tones = {
    info: "bg-blue-50 border-blue-200 text-blue-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    danger: "bg-red-50 border-red-200 text-red-900",
    success: "bg-green-50 border-green-200 text-green-900",
  };
  const Icon =
    tone === "warn"
      ? AlertTriangle
      : tone === "danger"
      ? AlertTriangle
      : tone === "success"
      ? CheckCircle
      : BookOpen;
  return (
    <div className={`rounded-md border p-3 my-3 flex gap-2 ${tones[tone]}`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="text-sm leading-relaxed">
        {title && <p className="font-semibold mb-1">{title}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function StaffGuide() {
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Staff User Manual</h1>
          <p className="text-slate-500">
            End-to-end operations guide for Admin, Assessor and IQA users —
            updated for the current IQA-gated workflow.
          </p>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="candidates" data-testid="tab-candidates">Candidates</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">AI Analysis</TabsTrigger>
            <TabsTrigger value="scoring" data-testid="tab-scoring">Scoring</TabsTrigger>
            <TabsTrigger value="interview" data-testid="tab-interview">Interviews</TabsTrigger>
            <TabsTrigger value="iqa" data-testid="tab-iqa">IQA Gate</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports & Badges</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger>
          </TabsList>

          {/* ============================ OVERVIEW ============================ */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" /> System Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <p>
                  <strong>Credentia AI</strong> is an AI-powered Recognition of
                  Prior Learning (RPL) platform. It assesses each candidate's
                  evidence against a competency framework and produces a
                  verifiable digital credential.
                </p>

                <SectionTitle>The 8-stage candidate lifecycle</SectionTitle>
                <ol className="list-decimal list-inside space-y-1">
                  <li><strong>Registration</strong> — Invite or self-apply, admin approval.</li>
                  <li><strong>Profile</strong> — Candidate completes 5 profile sections.</li>
                  <li><strong>Documents</strong> — Evidence uploaded to GridFS.</li>
                  <li><strong>AI Analysis</strong> — GPT-5.2 maps evidence to competencies.</li>
                  <li><strong>Assessor Scoring</strong> — Manual per-competency score.</li>
                  <li><strong>Interview</strong> — Live video or Written form.</li>
                  <li><strong>IQA Decision</strong> — Independent quality gate (approve / reject / send back).</li>
                  <li><strong>Award &amp; Credential</strong> — Vector PDF, QR code, LinkedIn share, 3-tier badges.</li>
                </ol>

                <Callout tone="info" title="What changed in this release">
                  Reports and credentials are now <strong>strictly gated by a
                  formal IQA decision</strong>. The "Generate Reports" button
                  unlocks only once IQA has recorded an approval. Candidates can
                  submit up to <strong>3 appeals</strong> against rejection
                  decisions.
                </Callout>

                <SectionTitle>Admin dashboard at a glance</SectionTitle>
                <Shot
                  src="/manual/admin-dashboard.png"
                  alt="Admin dashboard"
                  caption="Dashboard shows live KPIs and four notification chips: Pending Approval (amber), Profile Complete (green), Awaiting Profile (blue), Needs AI Analysis (purple). Click any chip to jump to the filtered Candidates list."
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ CANDIDATES =========================== */}
          <TabsContent value="candidates" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" /> Managing Candidates
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <SectionTitle>Three ways to add a candidate</SectionTitle>
                <ol className="list-decimal list-inside space-y-1.5">
                  <li>
                    <strong>Quick Invite</strong> — Click <em>Invite Candidate</em>{" "}
                    on the Dashboard or Candidates page. Enter name + email; the
                    system sends a login email with a temp password (default{" "}
                    <code>User@123</code>) and creates a draft candidate record.
                  </li>
                  <li>
                    <strong>Self-Registration</strong> — Candidate visits{" "}
                    <code>/apply</code>, signs in (Google / magic link) and
                    completes the registration form. They appear as{" "}
                    <em>Pending Approval</em>.
                  </li>
                  <li>
                    <strong>Full Admin Create</strong> — Use the full form on the
                    Candidates page when you already have all candidate details.
                  </li>
                </ol>

                <Shot
                  src="/manual/admin-candidates-list.png"
                  alt="Candidates list"
                  caption="The Candidates list. Use the status chips at the top to filter (Pending Approval, Profile, AI, Interview, IQA, Approved). Each row shows the case status, assigned assessor and IQA."
                />

                <Shot
                  src="/manual/admin-invite-modal.png"
                  alt="Quick invite modal"
                  caption="Quick-Invite modal — name + email is all you need. The candidate receives a branded email with their temporary password."
                />

                <SectionTitle>Approving pending candidates</SectionTitle>
                <p>
                  Self-registered candidates land in <em>Pending Approval</em>.
                  Click <em>Review</em>, assign an Institution and IPCB Board,
                  then approve to unlock their profile-completion flow.
                </p>

                <SectionTitle>Assigning Assessor &amp; IQA</SectionTitle>
                <p>
                  Use the Assessor and IQA dropdowns on the candidate row (or
                  detail page). Assigned staff receive an automatic email and
                  the candidate appears in their queue.
                </p>

                <SectionTitle>Candidate detail page</SectionTitle>
                <Shot
                  src="/manual/admin-candidate-detail.png"
                  alt="Candidate detail page"
                  caption="The candidate detail page is the operational cockpit: profile, documents, AI results, scoring, interview, IQA panel and report buttons — all in one scrollable view."
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ AI ANALYSIS ========================== */}
          <TabsContent value="ai" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" /> AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <p>
                  GPT-5.2 reads the candidate's profile data and every uploaded
                  document, then maps the evidence to the framework's
                  competencies.
                </p>

                <SectionTitle>Running an analysis</SectionTitle>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open the candidate detail page.</li>
                  <li>Select Framework + Qualification Level.</li>
                  <li>Click <em>Run AI Analysis</em>.</li>
                  <li>Wait ~30-90 seconds for the assessment to complete.</li>
                </ol>

                <SectionTitle>What you get back</SectionTitle>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Recommended award</strong> + 2 alternatives (dynamic to the candidate's industry).</li>
                  <li><strong>Confidence score</strong> — self-reported &amp; evidence-based.</li>
                  <li><strong>Per-competency coverage</strong>: Strong / Adequate / Partial / Weak / No Evidence.</li>
                  <li><strong>Gap report</strong> — exactly what's missing + recommended docs to request.</li>
                  <li><strong>Document authentication</strong> — name mismatch, date anomalies, duplicates flagged.</li>
                </ul>

                <Callout tone="warn" title="Request more info — max 3 rounds">
                  Use <em>Request More Info</em> to email the candidate a
                  specific request. The candidate must respond before you can
                  re-run analysis. Up to <strong>3 info-requests</strong> per
                  case; after that, scoring proceeds on the evidence on file.
                </Callout>

                <SectionTitle>Re-running after new evidence</SectionTitle>
                <p>
                  Re-running compares the new result against the previous one and
                  surfaces a delta: what improved, what changed, coverage gain.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ SCORING ============================== */}
          <TabsContent value="scoring" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-blue-600" /> Assessor Scoring
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <p>
                  After AI analysis the assessor records a manual decision per
                  competency. These scores are the authoritative input to the
                  badge engine and IQA review.
                </p>

                <SectionTitle>How to score</SectionTitle>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open the Competency Scoring panel on the candidate detail page.</li>
                  <li>For each competency: choose Pass / Refer / Fail, add evidence notes.</li>
                  <li>Save — the case status moves to <em>Awaiting Interview</em>.</li>
                  <li>Once you lock the scores, the 3-tier badges become eligible (badges issue automatically after IQA approval).</li>
                </ol>

                <Shot
                  src="/manual/admin-competencies.png"
                  alt="Competencies / framework management"
                  caption="Frameworks &amp; Competencies admin. Each competency has Evidence Required text; the better-defined this is, the more accurate the AI analysis."
                />

                <Callout tone="info" title="Qualification levels are now centrally managed">
                  Use the global <strong>Qualification Levels</strong> catalog
                  to keep level codes (e.g. L4, L5, Diploma, Advanced) and
                  numeric ranks consistent across every framework. This drives
                  the candidate's awarded level and the framework-level badge
                  tier.
                </Callout>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ INTERVIEW ============================ */}
          <TabsContent value="interview" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-600" /> Interview Process
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <p>
                  Interviews are triggered <strong>after</strong> assessor
                  scoring is recorded. Choose the mode based on the candidate's
                  location and availability.
                </p>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-800">
                    Option A — Live Interview
                  </h5>
                  <p className="text-sm">
                    For face-to-face or video call interviews.
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm mt-2">
                    <li>Click <em>Generate AI Interview Questions</em>.</li>
                    <li>Click <em>Schedule Live Interview</em>.</li>
                    <li>Set 3 date/time options, meeting link, industry-specialist email.</li>
                    <li>Candidate picks a slot from their dashboard.</li>
                    <li>Specialist receives questions + scoring link by email.</li>
                    <li>Conduct interview and record Pass / Fail.</li>
                  </ol>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mt-3">
                  <h5 className="font-semibold text-purple-800">
                    Option B — Written Interview
                  </h5>
                  <p className="text-sm">
                    Candidate answers in writing; assessor reviews remotely.
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm mt-2">
                    <li>Generate questions, click <em>Send Written Interview</em>.</li>
                    <li>Set the deadline + external assessor name/email.</li>
                    <li>Candidate gets a tokenised link — no login needed.</li>
                    <li>After submission, assessor receives a scoring link.</li>
                    <li>
                      Up to <strong>3 clarification questions</strong> can be
                      sent before the final Pass / Fail.
                    </li>
                    <li>
                      Need to resend? Use the new <em>Resend</em> button — it is
                      idempotent and reuses the same token.
                    </li>
                  </ol>
                </div>

                <Callout tone="info" title="Industry Specialist">
                  External assessors don't have a login. They score interviews
                  entirely through tokenised email links.
                </Callout>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ IQA GATE ============================= */}
          <TabsContent value="iqa" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" /> IQA Decision Gate
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <p>
                  IQA (Internal Quality Assurance) is the final, independent
                  check before any credential is issued. <strong>No report can
                  be generated</strong> until an IQA approval is recorded.
                </p>

                <SectionTitle>How IQA records a decision</SectionTitle>
                <ol className="list-decimal list-inside space-y-1">
                  <li>
                    Open the candidate detail page — once interview is Pass,
                    the IQA card becomes active.
                  </li>
                  <li>
                    Choose a decision:
                    <ul className="list-disc list-inside ml-5">
                      <li><strong>Approve</strong> — unlocks Report Generation.</li>
                      <li><strong>Reject at assessor stage</strong> — sends a stage-specific email; candidate may appeal.</li>
                      <li><strong>Reject at IQA stage</strong> — final IQA-level rejection email; candidate may appeal.</li>
                      <li><strong>Request more info</strong> — sends back to assessor for additional evidence.</li>
                    </ul>
                  </li>
                  <li>Add a written reason — this is logged in the audit trail and emailed to the candidate.</li>
                </ol>

                <Callout tone="warn" title="Appeal policy">
                  Each candidate can submit up to <strong>3 appeals</strong>{" "}
                  across their entire case. The dashboard shows the live{" "}
                  <em>Appeal X of 3</em> counter. After 3 appeals are exhausted,
                  the case is closed and the decision is final.
                </Callout>

                <SectionTitle>Audit trail</SectionTitle>
                <p>
                  Every IQA decision and appeal action is written to the audit
                  log against the candidate id with timestamp, actor and reason.
                  Use the <em>Maintenance</em> → <em>Audit</em> view to inspect.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ REPORTS / BADGES ====================== */}
          <TabsContent value="reports" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" /> Reports, Badges &amp; Sharing
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <SectionTitle>Generating reports</SectionTitle>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Pre-condition: IQA approval recorded (case status = <em>approved</em>).</li>
                  <li>Click <em>Generate Reports</em> on the candidate detail page.</li>
                  <li>
                    Two reports are produced:
                    <ul className="list-disc list-inside ml-5">
                      <li><strong>IPCB Report</strong> — for the professional body.</li>
                      <li><strong>Institution Report</strong> — for the awarding institution.</li>
                    </ul>
                  </li>
                  <li>Both are downloadable as vector PDF with a unique <code>Report ID</code> and QR code.</li>
                </ol>

                <Callout tone="info" title="Public verification">
                  Any third party can validate a credential by entering the
                  Report ID at <code>/verify</code>. The verify page renders an
                  elegant card with 3-tier badge display and LinkedIn share
                  buttons — printing is intentionally disabled for security.
                </Callout>

                <SectionTitle>Three-tier digital badge system</SectionTitle>
                <ul className="list-disc list-inside space-y-1.5">
                  <li>
                    <strong>Tier 1 — Competency Badges:</strong> auto-issued per
                    competency when the assessor's score crosses the configured
                    score floor.
                  </li>
                  <li>
                    <strong>Tier 2 — Professional Identity Badge:</strong> GPT-5.2
                    clusters a candidate's strongest competencies into a
                    recognised professional identity (e.g. "Senior Quality
                    Inspector").
                  </li>
                  <li>
                    <strong>Tier 3 — Framework Achievement Badge:</strong>{" "}
                    hybrid logic combining coverage % and average score:
                    <ul className="list-disc list-inside ml-5">
                      <li><strong>Distinction</strong> — coverage ≥ distinction threshold AND avg score ≥ floor.</li>
                      <li><strong>Achievement</strong> — coverage ≥ completion threshold AND avg score ≥ floor.</li>
                      <li><strong>Partial</strong> — partial completion below thresholds.</li>
                    </ul>
                  </li>
                </ul>

                <SectionTitle>LinkedIn integration</SectionTitle>
                <p>
                  The candidate's verify page exposes two buttons:{" "}
                  <em>Add to LinkedIn Profile</em> (uses Credentia AI's official
                  organisation ID <code>18074226</code>) and{" "}
                  <em>Share to LinkedIn Feed</em> (publishes a public post with
                  Open Graph thumbnail).
                </p>

                <Shot
                  src="/manual/admin-awards.png"
                  alt="Awards admin"
                  caption="The Awards catalog. Each award maps to a framework and is what the AI selects from when recommending a candidate's primary qualification."
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================ ROLES ============================== */}
          <TabsContent value="roles" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" /> Roles &amp; Permissions
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none text-slate-700">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="p-2 text-left border">Permission</th>
                        <th className="p-2 text-center border">Super Admin</th>
                        <th className="p-2 text-center border">Admin</th>
                        <th className="p-2 text-center border">Assessor</th>
                        <th className="p-2 text-center border">IQA</th>
                        <th className="p-2 text-center border">Industry Specialist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Manage Institutions / IPCBs", true, true, false, false, false],
                        ["Manage Competency Frameworks", true, true, false, false, false],
                        ["Manage Qualification Levels", true, false, false, false, false],
                        ["Manage Awards", true, true, false, false, false],
                        ["Manage Users", true, true, false, false, false],
                        ["Badge Settings", true, false, false, false, false],
                        ["Manage Candidates", true, true, true, false, false],
                        ["Run AI Analysis", true, true, true, false, false],
                        ["Score Competencies", true, true, true, true, false],
                        ["Generate Interview Questions", true, true, true, true, false],
                        ["Schedule / Send Interviews", true, true, true, true, false],
                        ["Score Interview Results", true, true, false, false, true],
                        ["Record IQA Decision", true, false, false, true, false],
                        ["Generate Reports (post-IQA)", true, true, false, true, false],
                        ["View Analytics", true, true, true, true, false],
                      ].map((row, idx) => {
                        const [perm, ...flags] = row;
                        return (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-2 border font-medium">{perm}</td>
                            {flags.map((v, i) => (
                              <td key={i} className="p-2 border text-center">
                                {v ? (
                                  <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                                ) : (
                                  "—"
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Industry Specialist has no login — they score interviews via
                  tokenised email links only.
                </p>

                <Shot
                  src="/manual/admin-users.png"
                  alt="User management"
                  caption="Users page — invite staff, assign roles, deactivate. Role changes take effect immediately."
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
